import { createGlobalStyle } from "styled-components";
import { palette, font } from "styled-theme";
// import 'antd/dist/antd.css';

const GlobalStyles = createGlobalStyle`
/* Task Board filter bar — neutralise the app-wide green outline on its inputs
   (grey instead), scoped to .taskboard-filter-bar. Base selectors (no pseudo)
   are included so the RESTING state is grey too — the search box showed green
   when not focused. Placed at the TOP of the template (this file has an
   unbalanced brace lower down that nests any rule appended at the end); higher
   specificity + !important beats the green rules regardless of order. */
.taskboard-filter-bar .ant-input,
.taskboard-filter-bar .ant-input:hover,
.taskboard-filter-bar .ant-input:focus,
.taskboard-filter-bar .ant-input-focused,
.taskboard-filter-bar .ant-input-affix-wrapper,
.taskboard-filter-bar .ant-input-affix-wrapper:hover,
.taskboard-filter-bar .ant-input-affix-wrapper:focus,
.taskboard-filter-bar .ant-input-affix-wrapper:focus-within,
.taskboard-filter-bar .ant-input-affix-wrapper-focused,
.taskboard-filter-bar .ant-picker,
.taskboard-filter-bar .ant-picker:hover,
.taskboard-filter-bar .ant-picker:focus,
.taskboard-filter-bar .ant-picker-focused,
.taskboard-filter-bar .ant-picker-input > input,
.taskboard-filter-bar .ant-picker-input > input:hover,
.taskboard-filter-bar .ant-picker-input > input:focus,
.taskboard-filter-bar .ant-select .ant-select-selector,
.taskboard-filter-bar .ant-select:not(.ant-select-disabled):hover .ant-select-selector,
.taskboard-filter-bar .ant-select-focused:not(.ant-select-disabled) .ant-select-selector {
  border-color: #d9d9d9 !important;
  box-shadow: none !important;
}
.taskboard-filter-bar .ant-picker .ant-picker-active-bar {
  background: #d9d9d9 !important;
}

/* App-wide input focus/hover reset. Replaces the old dark-green border +
   pale-yellow glow (and antd's green @primary-color focus on affix search
   boxes). HOVER is neutral (grey, no glow); FOCUS shows a subtle on-brand ring
   so keyboard / low-vision users keep a visible focus state (WCAG 2.4.7)
   without the loud yellow glow. Validation states (-status-error / -warning /
   etc.) are EXCLUDED via :not([class*="-status-"]) so antd keeps their
   red/orange borders — !important beats antd's non-important error rules, so
   they must be excluded rather than relying on specificity. Kept high in the
   file + !important so it wins regardless of the unbalanced brace lower down. */
.ant-input:not([class*="-status-"]):hover:not(:focus),
.ant-input-affix-wrapper:not([class*="-status-"]):hover:not(:focus-within),
.ant-input-number:not([class*="-status-"]):hover:not(:focus),
textarea.ant-input:not([class*="-status-"]):hover:not(:focus),
.ant-picker:not([class*="-status-"]):not(.ant-picker-focused):hover,
.ant-select:not(.ant-select-disabled):not(.ant-select-focused):not([class*="-status-"]):hover .ant-select-selector {
  border-color: #d9d9d9 !important;
  box-shadow: none !important;
}
.ant-input:not([class*="-status-"]):focus,
.ant-input-focused:not([class*="-status-"]),
.ant-input-affix-wrapper:not([class*="-status-"]):focus,
.ant-input-affix-wrapper:not([class*="-status-"]):focus-within,
.ant-input-affix-wrapper-focused:not([class*="-status-"]),
.ant-input-number:not([class*="-status-"]):focus,
.ant-input-number-focused:not([class*="-status-"]),
textarea.ant-input:not([class*="-status-"]):focus,
.ant-picker:not([class*="-status-"]):focus,
.ant-picker-focused:not([class*="-status-"]),
.ant-select-focused:not(.ant-select-disabled):not([class*="-status-"]) .ant-select-selector {
  border-color: #0B2A1B !important;
  box-shadow: 0 0 0 2px rgba(11, 42, 27, 0.12) !important;
  outline: none !important;
}
/* antd makes the inner input of an affix-wrapper (search boxes with a prefix or
   suffix icon) borderless, so only the wrapper draws the border. A styled-
   component input theme re-borders .ant-input at equal specificity and wins by
   source order, producing a DOUBLE border — an "outline inside the search bar".
   Restore the borderless, shadowless inner input app-wide so affix search boxes
   render a single clean border and the focus ring lands on the wrapper (not
   inside it). Placed after the focus reset so it wins on box-shadow. */
.ant-input-affix-wrapper > .ant-input,
.ant-input-affix-wrapper > input.ant-input,
.ant-input-affix-wrapper > .ant-input:hover,
.ant-input-affix-wrapper > .ant-input:focus {
  border: none !important;
  box-shadow: none !important;
}

/* On-path measurement labels. leaflet-measure-path ships 10px black text with a
   1px white halo. That is legible on the plugin demo's light OSM basemap, but
   not here: we render over dark tiles and satellite imagery, where a thin halo
   vanishes and only the black text remains.
   A halo cannot win on every basemap — dark text needs a light backing, light
   text needs a dark one, and this app has light, dark and satellite. So the
   label gets an opaque chip instead, which reads the same on all of them.
   !important because the plugin's own stylesheet sets these on the element and
   its injection order relative to styled-components is not guaranteed. */
.leaflet-container .leaflet-measure-path-measurement {
  color: #111827 !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-shadow: none !important;
}
.leaflet-container .leaflet-measure-path-measurement > div {
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(17, 24, 39, 0.25);
  border-radius: 3px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.35);
  display: inline-block;
  padding: 0;
  line-height: 1;
}

.leaflet-container .leaflet-measure-path-measurement > div[title*="Total area"] {
  background: transparent;
  text-shadow: 0px 0px 4px white;
}

/* Map furniture — north arrow + scale bar, shared by every map engine
   (Leaflet, Google Maps). See src/library/helpers/mapFurniture.js. Kept near
   the TOP for the same reason as the block above: the unbalanced brace lower
   down would nest these rules. The white card is what makes the black arrow
   readable over satellite imagery and dark tiles. */
.airagri-north-arrow {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  line-height: 0;
  margin: 10px;
  padding: 6px;
}
.airagri-north-arrow img {
  display: block;
}
/* When the map can rotate, the arrow tracks the bearing: drag it to turn the
   map, click it to face north. grab/grabbing is the affordance that says so. */
.airagri-north-arrow--compass {
  cursor: grab;
}
.airagri-north-arrow--compass:hover {
  background: #f3f4f6;
}
.airagri-north-arrow--dragging {
  cursor: grabbing;
}
/* Arrow above scale bar, as one column. The extra bottom margin clears the
   attribution row and the Google terms strip the satellite layer draws along
   the bottom edge — without it the scale bar ends up underneath them. The
   third class outweighs Leaflet's own .leaflet-bottom .leaflet-control margin. */
.airagri-map-furniture {
  align-items: flex-end;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.leaflet-bottom .airagri-map-furniture.leaflet-control {
  margin-bottom: 34px;
}
.airagri-map-furniture .airagri-north-arrow,
.airagri-map-furniture .leaflet-control {
  margin: 0;
}
.leaflet-control-scale-line,
.mapboxgl-ctrl-scale {
  background: #fff;
  border: 2px solid #374151;
  border-top: none;
  border-radius: 0 0 2px 2px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  color: #374151;
  font-size: 11px;
  font-weight: 600;
  line-height: 14px;
  padding: 2px 6px 1px;
  text-align: center;
  white-space: nowrap;
}
/* Leaflet draws a second scale line as the lower half of a stacked pair, with
   its ticks pointing up and a negative top margin. Only one of ours is ever
   visible, so draw both the same way up. */
.airagri-scale .leaflet-control-scale-line:not(:first-child) {
  border-top: none;
  border-bottom: 2px solid #374151;
  margin-top: 0;
}
/* Leaflet builds both unit lines (metric first, imperial second) and keeps both
   updated; the active class picks which one is visible. Swapping a class avoids
   rebuilding the control, which would make the bar jump between corners. */
.airagri-scale--metric .leaflet-control-scale-line:nth-child(2),
.airagri-scale--imperial .leaflet-control-scale-line:nth-child(1) {
  display: none;
}
/* The bar toggles metric/imperial on click, so it needs to look clickable. */
.airagri-scale,
.mapboxgl-ctrl-scale {
  cursor: pointer;
}
.airagri-scale:hover .leaflet-control-scale-line,
.mapboxgl-ctrl-scale:hover {
  background: #f3f4f6;
}
  .ant-btn{
    border-radius: 8px;
  }

  .hs-approval-btn {
    color: #0B2A1B !important;
    border-color: #0B2A1B !important;
    box-shadow: none !important;
  }

  .hs-approval-btn:hover,
  .hs-approval-btn:focus,
  .hs-approval-btn:active,
  .hs-approval-btn:focus-visible {
    color: #0B2A1B !important;
    border-color: #0B2A1B !important;
    box-shadow: none !important;
  }

  .hs-approval-btn-primary {
    background-color: #0B2A1B !important;
    border-color: #0B2A1B !important;
    box-shadow: none !important;
  }

  .hs-approval-btn-primary:hover,
  .hs-approval-btn-primary:focus,
  .hs-approval-btn-primary:active,
  .hs-approval-btn-primary:focus-visible {
    background-color: #0B2A1B !important;
    border-color: #0B2A1B !important;
    box-shadow: none !important;
  }

  .ant-upload-list-picture .ant-upload-list-item-thumbnail img {
    object-fit: contain;
  }

  .leaflet-tile-dark {
    .leaflet-tile {
      -webkit-filter: brightness(60%)!important;
    }
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .logo {
    font-size: 32px;
    font-weight: 700;
    color: #fff;
  }

  .ant-table-thead > tr.ant-table-row-hover:not(.ant-table-expanded-row) > td, .ant-table-tbody > tr.ant-table-row-hover:not(.ant-table-expanded-row) > td, .ant-table-thead > tr:hover:not(.ant-table-expanded-row) > td, .ant-table-tbody > tr:hover:not(.ant-table-expanded-row) > td {
    background: #f8f8f8!important;
  }

  .ant-row.ant-form-item {
    margin-bottom: 5px;
  }

  .has-success.has-feedback {
    .ant-select {
      .ant-select-selection {
        .ant-select-selection__rendered {
          .ant-select-selection__placeholder {
            display: none !important;
          }
        }
      }
    }
  }

  /*-----------------------------------------------*/
  // style for project category menu [ScrumBoard]
  /*-----------------------------------------------*/
  .project-category {
    .ant-select-dropdown-menu {
      .ant-select-dropdown-menu-item {
        padding: 8px 12px;
        color: #000000;
        font-family: 'Roboto';
        font-weight: 400;
      }
    }
  }

  /*-----------------------------------------------*/
  // style for project menu [ScrumBoard]
  /*-----------------------------------------------*/
  .ant-dropdown {
    &.project-menu {
      width: 280px;
      top: 133px !important;

      .ant-dropdown-menu {
        padding: 0;
        overflow: hidden;

        .ant-dropdown-menu-item {


          min-height: 54px;
          line-height: auto;
          display: flex;
          align-items: center;
          padding: 10px 20px;

          &:first-child {
            padding: 0;
            border-bottom: 1px solid #f4f6fd;

            &:hover,
            &:focus {
              background-color: #ffffff;
            }
          }

          &:hover,
          &:focus {
            background-color: #F3F5FD;
          }

          &:last-child {
            background-color: #E6EAF8;
          }
        }
      }
    }
  }

  /*-----------------------------------------------*/
  // style for popover [ScrumBoard]
  /*-----------------------------------------------*/
  .ant-popover {
    .ant-checkbox-group {
      display: flex;
      flex-direction: column;
      .ant-checkbox-group-item {
        margin: 5px 0;
        span {
          font-size: 14px;
          color: #788195;
          text-transform: capitalize;
        }
      }
    }
  }

  /*-----------------------------------------------*/
  // style for modal [ScrumBoard]
  /*-----------------------------------------------*/
  .ant-modal-wrap {
    .ant-modal {
      .ant-modal-content {
        .ant-modal-body {
          .render-form-wrapper {
            padding: 10px;
            h2 {
              margin: 0;
            }
            form {
              padding: 15px 0 3px;
              .field-container {
                margin-bottom: 26px;
              }
            }
          }
        }
      }
    }
  }


/*-----------------------------------------------*/
  // style form previous GlobalStyles
  /*-----------------------------------------------*/

  .ant-table-thead > tr.ant-table-row-hover:not(.ant-table-expanded-row) > td, .ant-table-tbody > tr.ant-table-row-hover:not(.ant-table-expanded-row) > td, .ant-table-thead > tr:hover:not(.ant-table-expanded-row) > td, .ant-table-tbody > tr:hover:not(.ant-table-expanded-row) > td {
    background: #f8f8f8!important;
}

font-family: ${font("primary", 0)};

h1,
h2,
h3,
h4,
h5,
h6,
a,
p,
li,
input,
textarea,
span,
div,
img,
svg {
  &::selection {
    background: ${palette("primary", 0)};
    color: #fff;
  }
}

.ant-row:not(.ant-form-item) {
  ${
    "" /* margin-left: -8px;
  margin-right: -8px; */
  };
  &:before,
  &:after {
    display: none;
  }
}

.ant-row > div {
  padding: 0;
}

.isoLeftRightComponent {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.isoCenterComponent {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}
/********** Add Your Global CSS Here **********/

body {
  -webkit-overflow-scrolling: touch;
}

html h1,
html h2,
html h3,
html h4,
html h5,
html h6,
html a,
html p,
html li,
input,
textarea,
span,
div,
html,
body,
html a {
  margin-bottom: 0;
  font-family: 'Roboto', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.004);
}

html ul {
  -webkit-padding-start: 0px;
  list-style: none;
  margin-bottom: 0;
}

.scrollbar-track-y,
.scrollbar-thumb-y {
  width: 5px !important;
}

.scrollbar-track-x,
.scrollbar-thumb-x {
  height: 5px !important;
}

.scrollbar-thumb {
  border-radius: 0 !important;
}

.scrollbar-track {
  background: rgba(222, 222, 222, 0.15) !important;
}

.scrollbar-thumb {
  border-radius: 0 !important;
  background: rgba(0, 0, 0, 0.5) !important;
}

.ant-popover-placement-bottom > .ant-popover-content > .ant-popover-arrow:after,
.ant-popover-placement-bottomLeft
  > .ant-popover-content
  > .ant-popover-arrow:after,
.ant-popover-placement-bottomRight
  > .ant-popover-content
  > .ant-popover-arrow:after,
.ant-popover-placement-top > .ant-popover-content > .ant-popover-arrow:after,
.ant-popover-placement-topLeft
  > .ant-popover-content
  > .ant-popover-arrow:after,
.ant-popover-placement-topRight
  > .ant-popover-content
  > .ant-popover-arrow:after {
  left: 0;
  margin-left: -4px;
}

/* Instagram Modal */

.ant-modal-wrap.instagram-modal .ant-modal {
  max-width: 935px;
  width: 100% !important;
}

@media only screen and (max-width: 991px) {
  .ant-modal-wrap.instagram-modal .ant-modal {
    padding: 0 60px;
  }
}

@media only screen and (max-width: 767px) {
  .ant-modal-wrap.instagram-modal .ant-modal {
    max-width: 580px;
  }
}

.ant-modal-wrap.instagram-modal .ant-modal-content {
  border-radius: 0;
}

.ant-modal-wrap.instagram-modal .ant-modal-content button.ant-modal-close {
  position: fixed;
  color: #fff;
}

.ant-modal-wrap.instagram-modal .ant-modal-content button.ant-modal-close i {
  font-size: 24px;
}

.ant-modal-wrap.instagram-modal .ant-modal-content .ant-modal-body {
  padding: 0;
}

/********** Add Your Global RTL CSS Here **********/

/* Popover */

html[dir='rtl'] .ant-popover {
  text-align: right;
}

/* Ecommerce Card */

html[dir='rtl'] .isoCardInfoForm .ant-input {
  text-align: right;
  border: 1px solid #0B2A1B!important;
  box-sizing: border-box!important;
  border-radius: 8px !important;
}

/* Modal */

html[dir='rtl'] .has-success.has-feedback:after,
html[dir='rtl'] .has-warning.has-feedback:after,
html[dir='rtl'] .has-error.has-feedback:after,
html[dir='rtl'] .is-validating.has-feedback:after {
  left: 0;
  right: auto;
}

html[dir='rtl'] .ant-modal-close {
  right: inherit;
  left: 0;
}

html[dir='rtl'] .ant-modal-footer {
  text-align: left;
}

html[dir='rtl'] .ant-modal-footer button + button {
  margin-left: 0;
  margin-right: 8px;
}

html[dir='rtl'] .ant-confirm-body .ant-confirm-content {
  margin-right: 42px;
}

html[dir='rtl'] .ant-btn > .anticon + span,
html[dir='rtl'] .ant-btn > span + .anticon {
  margin-right: 0.5em;
}

html[dir='rtl'] .ant-btn-loading span {
  margin-left: 0;
  margin-right: 0.5em;
}

html[dir='rtl']
  .ant-btn.ant-btn-loading:not(.ant-btn-circle):not(.ant-btn-circle-outline) {
  padding-left: 25px;
  padding-right: 29px;
}

html[dir='rtl']
  .ant-btn.ant-btn-loading:not(.ant-btn-circle):not(.ant-btn-circle-outline)
  .anticon {
  margin-right: -14px;
  margin-left: 0;
}

/* Confirm */

html[dir='rtl'] .ant-modal.ant-confirm .ant-confirm-body > .anticon {
  margin-left: 16px;
  margin-right: 0;
  float: right;
}

html[dir='rtl'] .ant-modal.ant-confirm .ant-confirm-btns {
  float: left;
}

html[dir='rtl'] .ant-modal.ant-confirm .ant-confirm-btns button + button {
  margin-right: 10px;
  margin-left: 0;
}

/* Message */

html[dir='rtl'] .ant-message .anticon {
  margin-left: 8px;
  margin-right: 0;
}

/* Pop Confirm */

html[dir='rtl'] .ant-popover-message-title {
  padding-right: 20px;
  padding-left: 0;
}

html[dir='rtl'] .ant-popover-buttons {
  text-align: left;
}

/* Notification */

html[dir='rtl']
  .ant-notification-notice-closable
  .ant-notification-notice-message {
  padding-left: 24px;
  padding-right: 0;
}

html[dir='rtl']
  .ant-notification-notice-with-icon
  .ant-notification-notice-message,
html[dir='rtl']
  .ant-notification-notice-with-icon
  .ant-notification-notice-description {
  margin-right: 48px;
}

html[dir='rtl'] .ant-notification-notice-close {
  right: auto;
  left: 16px;
}

html[dir='rtl'] .ant-notification-notice-with-icon {
  left: 0;
}

/* Dropzone */

html[dir='rtl'] .dz-hidden-input {
  display: none;
}

.deleteButton:hover{
  background-color:#f5f5f5;
}

.action-button.ant-btn:hover, .action-button.ant-btn:focus{
  color:#0B2A1B;
  border-color:#0B2A1B;
}
.ant-input:focus, .ant-input-focused, .ant-select:focus, .ant-select-focused, .ant-input-number-focused, .ant-input-number:focus{
  box-shadow: none;
}

.cancel-button.ant-btn:hover, .history-button:hover, .history-button:focus{
  color:#0B2A1B;
  border-color:#0B2A1B;
}
.ant-modal-content{
  border-radius: 10px;
}
.ant-modal-content .ant-modal-header {
  background-color: #0B2A1B;
  border-top-left-radius: 10px;
    border-top-right-radius: 10px;
}
.ant-modal-footer {
  display: flex;
}
.ant-modal-content .ant-modal-footer .cancel-button {
  border-radius : 8px;
  background-color: #ABABAB;
  border: #0000;
  color: #fff;
  // font-family: Inter;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  height: auto;
  line-height: 24px;
  padding: 8px;
  text-align: center;
  width: 50%;
}
.ant-modal-content .ant-modal-footer .save-button {
  border-radius : 8px;
  background-color: #0B2A1B;
  border: #0000;
  color: #fff;
  // font-family: Inter;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  height: auto;
  line-height: 24px;
  padding: 8px;
  text-align: center;
  width: 50%;
}
label.ant-form-item-required{
  color: #344054;
    font-size: 12px;
    font-weight: 500;
    line-height: 20px;
    letter-spacing: 0em;
    text-align: left;
}
.ant-modal-content .ant-modal-header .ant-modal-title {
  color: #FFFFFF;
}
.ant-modal-content .ant-modal-close {
  color: #FFFFFF;
}
.ant-modal-content .ant-modal-close .ant-modal-close-x span {
  border: 1.5px solid #FFFFFF;
  border-radius: 50%;
  padding: 3px;
}

.ant-modal-title {
word-wrap: break-word;
color: #000000d9;
font-size: 16px;
font-weight: 500;
line-height: 22px;
margin: 0;
}
.ant-select-selector{
  border-radius: 5px !important;
}
.ant-select-focused.ant-select-single:not(.ant-select-customize-input) .ant-select-selector{
 border-color : 1px solid #d9d9d9;
  border-radius : 8px !important;
  box-shadow: 0 0 0 2px #faf5c8
}
.ant-picker:hover, .ant-picker-focused{
  box-shadow: 0 0 0 2px #faf5c8;
}
input{
  border-radius : 5px !important;
}
label{
font-size: 12px !important;
font-weight: 500;
line-height: 20px;
letter-spacing: 0em;
text-align: left;
color : #344054;

}
.ant-picker{
  border-radius : 5px !important;
}
.ant-checkbox-disabled.ant-checkbox-checked .ant-checkbox-inner::after {
  border-color: #F5F5F5;
}

.sidebar-icon{
  // stroke: #0B2A1B !important;
  stroke: rgb(143, 148, 151) !important;
  margin-right: 10px;
  opacity: 1;
  display: inline-flex;
}

.ant-layout-sider-collapsed .sidebar-icon{
  min-width: 22px;
  margin-right: 10px;
  display: inline-flex;
  stroke:rgb(143, 148, 151) !important;
}

#custom-menu-icon:hover{
.sidebar-icon{
  opacity: 0.7;
}
}

.ant-menu-item-selected .sidebar-icon{
opacity: 1;

g.sidebar-icon {
  fill: #0B2A1B !important;
  stroke: #0B2A1B !important;
}
svg path {
  stroke: #0B2A1B !important;
  .fillClass{
    fill : #0B2A1B !important;
  }

}

}
.sidebar-icon{
  margin-right: 9px;
}

.sidebar-icon.visitors-icon{
  margin-right: 19px;
  display:flex;
}

.ant-menu-item-selected .sidebar-icon.visitors-icons svg path:first-child {
  stroke: none;
}

.sidebar-icons g{
  margin-right : 9px ;
  fill: rgb(143, 148, 151) !important;

  stroke-width:2 !important;
  stroke-linecap:round !important;
  stroke-linejoin:round !important;
}
.sidebar-icons g:active{
  fill:#0B2A1B !important;

}
.sidebar-icons{
  margin-right : 9px;
}
.ant-menu-item-selected .sidebar-icons{

  opacity: 1;
  g.sidebar-icons{
    fill:#0B2A1B !important;
}
.fc-cal-header-container{
  background-color: #0B2A1B!important;
}

.fc-cal-user-selecteddatedefault{
  background-color: #0B2A1B!important;
}

.fc-cal-user-selecteddatedefault:hover{
  background-color: #0B2A1B!important;
}
#zoom-default {
  position: absolute;
  bottom: 70px;
  left: 9px;
  z-index: 400;
  width: 28px;
  height: 28px;
  padding: unset!important;
}

#zoom-default:hover{
    cursor: pointer;
}

.ant-checkbox-group-item {
    display: block;
    margin-right: 0;
}

.ant-input-number, .ant-input, .ant-picker, .react-tagsinput {
  border: 1px solid #e9e9e9;
  box-sizing: border-box !important;
  border-radius: 5px !important;
}
.ant-input{
  border: 1px solid #e9e9e9;
  box-sizing: border-box !important;
  border-radius: 5px !important;
}

.ant-modal-title {
  color: #0B2A1B;
}

.ant-btn {
  border-radius: 20px!important;
}
.ant-select-single.ant-select-lg:not(.ant-select-customize-input) .ant-select-selector {
  border-radius : 8px !important;
}
.ant-modal-content, .ant-modal-header {
  border-radius: 10px;
}

/* Date range picker yellow highlights - custom, not handled by theme */
.ant-picker-dropdown .ant-picker-cell-range-end > .ant-picker-cell-inner{
  background: rgb(239, 203, 25) !important;
}

.ant-picker-cell-in-view.ant-picker-cell-in-range::before {
  background: #fdffd6;
}
.ant-picker-cell-in-view.ant-picker-cell-range-end:not(.ant-picker-cell-range-end-single)::before {
  background: #fdffd6;
}
.ant-picker-cell-in-view.ant-picker-cell-range-start:not(.ant-picker-cell-range-start-single)::before{
  background: #fdffd6;
}
.ant-picker-cell-in-view.ant-picker-cell-range-start .ant-picker-cell-inner{
  background: rgb(239, 203, 25) !important;
}
.menu-item-custom.ant-menu-item {
  &:active {
    background-color: #f5f5f5;
  }
}
.menu-item-custom.ant-menu-item-selected {
  background-color: #f5f5f5 !important;
}
.ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
  color: #3B82F6;
}
.ant-pagination-item-active a{
  background-color: #0B2A1B;
    color: white ;
    border-color: #0B2A1B !important;
}
.ant-pagination-item-active{
  border-color: #0B2A1B !important;
}
.ant-pagination-item-active:hover a {
  color: #ffffff !important;
}

/* Select dropdown selected items - custom highlight */
.ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
  background-color: #0B2A1B !important;
  color: #FFFFFF !important;
  font-weight: 600;
}

.ant-select-item-option-selected:not(.ant-select-item-option-disabled):hover {
  background-color: rgba(11, 42, 27, 0.85) !important;
  color: #FFFFFF !important;
}

/* Dropdown selected items */
.ant-dropdown-menu-item-selected,
.ant-dropdown-menu-item-selected > a,
.ant-dropdown-menu-submenu-title-selected,
.ant-dropdown-menu-submenu-title-selected > a {
  background-color: #0B2A1B !important;
  color: #FFFFFF !important;
}

/* Dropdown items hover state */
.ant-dropdown-menu-item:hover,
.ant-dropdown-menu-submenu-title:hover,
.ant-dropdown-menu-item.ant-dropdown-menu-item-active,
.ant-dropdown-menu-item.ant-dropdown-menu-submenu-title-active,
.ant-dropdown-menu-submenu-title.ant-dropdown-menu-item-active,
.ant-dropdown-menu-submenu-title.ant-dropdown-menu-submenu-title-active {
  color: #0B2A1B !important;
}

.ant-dropdown-menu-item-selected:hover,
.ant-dropdown-menu-submenu-title-selected:hover {
  background-color: rgba(11, 42, 27, 0.85) !important;
  color: #FFFFFF !important;
}

/* Cascader selected items */
.ant-cascader-menu-item-active:not(.ant-cascader-menu-item-disabled),
.ant-cascader-menu-item-active:not(.ant-cascader-menu-item-disabled):hover {
  background-color: #0B2A1B !important;
  color: #FFFFFF !important;
}

/* Tree select selected items */
.ant-tree-select-item-option-selected:not(.ant-tree-select-item-option-disabled) {
  background-color: #0B2A1B !important;
  color: #FFFFFF !important;
  font-weight: 600;
}

/* Transfer selected items */
.ant-transfer-list-content-item-checked {
  background-color: #0B2A1B !important;
  color: #FFFFFF !important;
}
`;

export default GlobalStyles;
